import { Column, SummaryItem } from "@devexpress/dx-react-grid";
import React from "react";

export type RouteItem = {
  path: string;
  name: string;
  icon: string;
  component: React.ComponentType<any>;
  layout: "/admin" | "/auth";
  isSidebarItem: boolean;
  allow?: UserPermissions[];
};

export enum OdOrc {
  OD = "OD",
  ORC = "ORC",
}

export enum ProjectStatus {
  Deferred = "Deferred",
  Unbilled = "Unbilled",
}

export enum ProjectFinishedStatus {
  False = "False",
  "ORC Finished" = "ORC Finished",
  "OD Finished" = "OD Finished",
}

export enum ProjectReplyStatus {
  revTeam = "revTeam", // pending from our side
  opsTeam = "opsTeam", // pending from PM's side
}

export type ProjectData = {
  code: string;
  startDate: string;
  endDate: string;
  pm: string;
  projName: string;
  _id: string;
  // normalizedName: string;
  workflowCode: string;
  vertical: string;
  lob: string;
  deliveryName: string;
  projectGroup: string;
  odOrc: OdOrc;
  odOrcBreakup: string;
  entity: string;
  projCurr: string;
  gm: string;
  avp: string;
  companyCurr: string;
  status: ProjectStatus;
  finishedStatus: ProjectFinishedStatus;
  financeRemarks?: string;
  pendingBilling: number;
  volumeDiscount: number;
  negPos: number;
  isFollowUpInProgress?: boolean;
  hasFollowUpInitiated: boolean;
  replyStatus?: ProjectReplyStatus;
  lastActivityAt?: string;
};

export type ProjectMonthlyData = {
  projectCode: string;
  year: number;
  month: number;
  openingBalanceCC: number;
  closingBalanceCC: number;
  accrualAmt: number;
  invoicingAmt: number;
  usdAmt: number;
  netAmtUsd: number;
};

export type EmailMessage = {
  _id: string;
  conversationId: string;
  messageId: string;
  subject: string;
  body: string;
  from: string;
  to: string[];
  cc: string[];
  webLink?: string;
  userId?: string;
  aggId?: string;
  createdAt: string;
};

export type GridResponse = {
  columns: Column[];
  currencyColumns: string[];
  dateColumns: string[];
  summaryItems: SummaryItem[];
};

export type AllUnbilledProjectsResponse = GridResponse & {
  projects: Pick<
    ProjectData,
    "_id" | "code" | "startDate" | "endDate" | "pm"
  >[];
};

export type AllProjectsResponse = GridResponse & {
  projects: ProjectData[];
};

export type AllActiveProjectsResponse = GridResponse & {
  projects: Pick<
    ProjectData,
    | "_id"
    | "code"
    | "startDate"
    | "endDate"
    | "pm"
    | "lastActivityAt"
    | "replyStatus"
    | "status"
  >[];
};

export type ProjectResponse = {
  data:
    | undefined
    | (ProjectData & {
        monthlyData0m_ago?: Pick<
          ProjectMonthlyData,
          "year" | "month" | "openingBalanceCC" | "closingBalanceCC"
        >;
        monthlyData1m_ago?: Pick<
          ProjectMonthlyData,
          "year" | "month" | "openingBalanceCC" | "closingBalanceCC"
        >;
        monthlyData2m_ago?: Pick<
          ProjectMonthlyData,
          "year" | "month" | "openingBalanceCC" | "closingBalanceCC"
        >;
      });
};

export type ProjectEmailsResponse = {
  messages: EmailMessage[];
  total: number;
  hasMore: boolean;
};

export type UpdateProjectRequest = Pick<
  ProjectData,
  "code" | "negPos" | "volumeDiscount" | "financeRemarks"
>;

export type ProjectScheduledFollowUpResponse = {
  _id: string;
  userData: { name: string };
  projectCode: string;
  content: string;
  ts: string;
  replyAll: boolean;
  cc: string[];
};

// export type NormalizedMappingType = {
//   _id: string;
//   clientName: string;
//   normalizedName: string;
// };

// export type AddNormalizedMappingReq = {
//   clientName: string;
//   normalizedName: string;
// };

// export type UpdateNormalizedMappingReq = {
//   _id: string;
// } & AddNormalizedMappingReq;

export type User = {
  id: string;
  name: string;
  email: string;
  roles: UserPermissions[];
  createdBy?: string;
};

export enum UserPermissions {
  admin = "admin",
  read = "read",
  write = "write",
  qna = "qna",
}

export type LoginResp = {
  token: string;
  user: User;
};

export enum ConfType {
  lshc = "lshc",
  financial = "financial",
  generic = "generic",
}

export type SentimentType = "Positive" | "Negative" | "Neutral";

export type UnbilledMonthlyReportResponse = {
  year: number;
  month: number;
  sum: number;
}[];

export type FollowUpsMonthlyReportResponse = UnbilledMonthlyReportResponse;

export type AnalyseChemicalsResponse = "processing" | "error";

export type AdminUserHeaderInfoResponse = {
  total: number;
};

export type AdminUsersList = {
  data: User[];
  total: number;
};

export type HeaderAggDataResponse = {
  numRecordings: number;
  numOngoingRecordings: number;
  numFailedRecordings: number;
  avgTimeToExecuteInSeconds: number;
  totalUsers: number;
  totalTimeToExecuteInSeconds: number;
  totalScreenshots: number;
};

export enum ExecutionStatus {
  recording = "recording",
  // assemblyAI get chapters & transcription
  transcribing = "transcribing",
  // screenshot and dedupe
  screenshotting = "screenshotting",
  // wordcloud and pdf step
  analysing = "analysing",
  // summary from bert, xlnet and gpt2 step
  summarizing = "summarizing",
  completed = "completed",
  error = "error",
}

export type ChemicalSearchResponse = {
  data: {
    chemicalCAS: string;
    chemicalName: string;
    chemicalSynonyms?: string;
    executionStatus: ExecutionStatus;
    executionTimeTakenInSeconds?: number;
    executionStartedAt: string;
    userName: string;
    userId: number;
    groupName: string;
    groupId: number;
    executionId: number;
    chemicalId: number;
  }[];
  total: number;
};

export type ExecutionInfoResponse = {
  lrc?: string;
  wordcloudUrls?: string[];
  keySentences?: string[];
  videoFileUrl?: string;
  chaptersUrl?: string;
  highlightFileUrl?: string;
  subtitlesUrl?: string;
  subtitlesJsonUrl?: string;
  subtitlesTextUrl?: string;
  analysisFileUrl?: string;
  summaryFileUrl?: string;
  earningsSummaryReportUrl?: string;
  utternacesJSONUrl?: string;
  sentiments?: [string, SentimentType][];
  entities?: {
    person: [string, number][];
    org: [string, number][];
    gpe: [string, number][];
    date: [string, number][];
    money: [string, number][];
  };
  execution: {
    id: number;
    confName: string;
    sessionName: string;
    timeTakenInSeconds: number;
    status: ExecutionStatus;
    startingState: ExecutionStatus;
    errorMessage?: string;
    slug: string;
    startedAt: string;
    endedAt: string;
    type: ConfType;
    user: {
      name: string;
    };
  };
};

export type AllCurrentExecutionsResponse = {
  data: {
    id: number;
    confName: string;
    steps?: ExecutionSteps[];
    sessionName: string;
    status?: ExecutionStatus;
    startingState: ExecutionStatus;
    errorMessage?: string;
    slug: string;
    startedAt: string;
    endedAt: string;
    user: {
      name: string;
    };
  }[];
  total: number;
};

export type AllPastExecutionsResponse = {
  data: {
    id: number;
    confName: string;
    sessionName: string;
    timeTakenInSeconds: number;
    status: ExecutionStatus;
    startingState: ExecutionStatus;
    errorMessage?: string;
    slug: string;
    startedAt: string;
    endedAt: string;
    user: {
      name: string;
    };
  }[];
  total: number;
};

export type AllEarningsCallTranscriptsList = any

export type EarningsCallTranscript = any

export interface SecDocument {
  created_at: string;
  id: string;
  updated_at: string;
  // metadata_map: BackendMetadataMap;
  url: string;
  identifierName?: string;
}

export interface response {
  id?: string;
  identifierId?: string;
  question?: string;
  response?: string;
  status?: string;
  created_at?: Date;
  type?: string;
  message?: string;
  responses?: string;
  index? : number;
  time?: string;
  identifierName: string;
  comparisonResultId?: string;
}

export type Response = response[];

export enum SearchResultStatus {
  error = "error",
  success = "success",
}

export type ChemicalExecutionInfoResponse = {
  total: number;
  data: {
    websiteId: number;
    websiteUrl: string;
    searchTerm?: string;
    found: boolean;
    searchStatus: SearchResultStatus;
    errorMessage?: string;
    screenshotUrl?: string;
    pageUrl: string;
    startedAt: string;
    searchTimeTakenInSeconds?: number;
  }[];
  executionInfo?: {
    executionId: number;
    chemicalSynonymsUsedForSearch?: string | undefined;
    timeTakenInSeconds?: number | undefined;
    status: ExecutionStatus;
    startedAt: string;
    groupId: number;
    groupName: string;
  };
};

export type GroupInfoResponse = {
  name: string;
  id: number;
  websites: { id: number; url: string }[];
  userName: string;
  userId: number;
  lastEditedBy: string;
  lastEditedById: number;
  createdAt: string;
  updatedAt: string;
};

export type AllGroupsResponse = {
  data: {
    lastEditedBy: string;
    lastEditedById: number;
    user: string;
    userId: string;
    name: string;
    id: number;
    createdAt: string;
    websites: { id: number; url: string }[];
  }[];
  total: number;
};

export type AllWebsitesResponse = {
  id: number;
  url: string;
}[];

export type GroupSuggestionsResponse = {
  name: string;
  id: number;
}[];

export type ChemicalSuggestionsResponse = {
  cas: string;
  name: string;
  synonyms?: string;
  id: number;
}[];

export type WebsiteStatsIndividualEntry = {
  url: string;
  totalSearches: number;
  totalChemicalsSearched: number;
  totalYes: number;
  totalNo: number;
  totalErrors: number;
};

export type WebsiteStatsResponse = {
  mostSearchedAndErrorWebsites: WebsiteStatsIndividualEntry[];
  mostSearchedAndFoundWebsites: WebsiteStatsIndividualEntry[];
  mostSearchedAndNotFoundWebsites: WebsiteStatsIndividualEntry[];
};

export enum ExecutionSteps {
  screenshot = "screenshot",
  transcribe = "transcribe",
  speakerTagging = "speakerTagging",
}

export enum DeleteSteps {
  video = "video",
  screenshots = "screenshots",
}

export type QnAResponse = {
  cost: number;
  message: string;
  sources: {
    value: string;
    original_value: string;
    index: number;
    url: string;
    page_no: number;
  }[][];
  responses: string[];
};


export type GetQuestionsResponse = {
  userId: number;
  id: number;
  question: string;
  executionId: number;
  qaResponse?: QnAResponse;
  createdAt: string;
  user: {
    name: string;
  };
}[];